import React from 'react'
import type { useDentalStreamingVoice } from '@hooks/useDentalStreamingVoice'
import ChartingVoiceStatus, {
  VoicePromptGroup,
} from '@components/pages/Charting/ChartingVoiceStatus.component'

interface DentalVoiceStatusProps {
  voice: Pick<
    ReturnType<typeof useDentalStreamingVoice>,
    | 'phase'
    | 'keyword'
    | 'backlog'
    | 'message'
    | 'history'
    | 'liveText'
    | 'queued'
  >
}

export const DENTAL_VOICE_QUICK_PROMPTS: string[] = [
  'Tooth 14 caries D2 mesial occlusal',
  'Filling composite mesial distal',
  'Crown zirconia',
  'Select number 1',
  'Next',
  'Undo',
  'Pause',
  'Resume',
]

export const DENTAL_VOICE_PROMPT_GROUPS: VoicePromptGroup[] = [
  {
    label: 'Restorations',
    prompts: [
      'Filling composite mesial occlusal distal',
      'Crown zirconia',
      'Veneer ceramic',
      'Inlay gold occlusal',
      'Sealants',
    ],
  },
  {
    label: 'Caries and wear',
    prompts: [
      '14 caries D2 mesial occlusal',
      'Recurring caries E1 distal',
      'Attrition moderate',
      'Abrasion slight buccal',
      'Erosion palatal',
    ],
  },
  {
    label: 'Endo and perio',
    prompts: [
      'Root canal',
      'Furcation class 2',
      'Recession class 1',
      'Periradicular moderate',
      'Crack vertical crown buccal',
    ],
  },
  {
    label: 'Prosthetics',
    prompts: [
      'Missing',
      'Implant',
      'Bridge zirconia mesial',
      'Abutment mesial',
      'Fracture mesiobuccal',
      'Fracture MOB',
      'Fracture MIFP',
      'Fracture I',
    ],
  },
  {
    label: 'Navigation',
    prompts: [
      'Jump to number 1',
      'Select number 1',
      'Jump to tooth 8',
      'Select tooth 3',
      'Next',
      'Back',
      'Undo',
      'Clear filling',
      'Pause',
      'Resume',
      'Stop',
    ],
  },
]

const DentalVoiceStatus = ({
  voice,
}: DentalVoiceStatusProps): React.ReactElement => {
  const active = ['connecting', 'listening', 'processing'].includes(voice.phase)
  const paused = active && voice.keyword.paused

  return (
    <ChartingVoiceStatus
      phase={voice.phase}
      backlog={voice.backlog}
      message={voice.message}
      history={voice.history}
      liveText={voice.liveText}
      queued={voice.queued}
      paused={paused}
      ariaLabel="Dental voice transcription"
      className="dental-voice-status"
      promptGroupsClassName="dental-voice-prompt-groups"
      stateExtra={<span>Tooth {voice.keyword.cursor.tooth}</span>}
      quickPrompts={DENTAL_VOICE_QUICK_PROMPTS}
      promptGroups={DENTAL_VOICE_PROMPT_GROUPS}
      tip="Include type, material, and surfaces as needed. Universal teeth 1–32. Pause briefly between phrases."
    />
  )
}

export default DentalVoiceStatus
